/**
 * Lookups run in the browser because FMCSA blocks the server's data-center
 * address range (HTTP 403 from Railway). Do not move this to the backend
 * without testing from production.
 */

import { computeMcs150NextDueDate } from './mcs150Schedule';

const FMCSA_CARRIER_URL = 'https://mobile.fmcsa.dot.gov/qc/services/carriers';
const FMCSA_UNAVAILABLE =
  'FMCSA lookup is temporarily unavailable. Please try again shortly.';

const OperatingStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  OUT_OF_SERVICE: 'OUT_OF_SERVICE',
};

const AuthorityType = {
  CARRIER: 'CARRIER',
  BROKER: 'BROKER',
  FREIGHT_FORWARDER: 'FREIGHT_FORWARDER',
};

function mapCarrierToResult(carrier) {
  let authorityType = AuthorityType.CARRIER;
  if (carrier.carrierOperation?.carrierOperationDesc) {
    const desc = carrier.carrierOperation.carrierOperationDesc.toLowerCase();
    if (desc.includes('broker')) {
      authorityType = AuthorityType.BROKER;
    } else if (desc.includes('freight forwarder') || desc.includes('forwarder')) {
      authorityType = AuthorityType.FREIGHT_FORWARDER;
    }
  }

  let operatingStatus = OperatingStatus.ACTIVE;
  if (carrier.allowedToOperate === 'N') {
    operatingStatus = OperatingStatus.INACTIVE;
  } else if (carrier.oosDate || carrier.outOfServiceDate) {
    operatingStatus = OperatingStatus.OUT_OF_SERVICE;
  }

  const dotNumber = carrier.dotNumber?.toString() || '';

  let mcs150Date;
  let mcs150LastUpdateDate;
  let mcs150NextDueDate;
  const mcs150Source = carrier.mcs150FormDate || carrier.snapshotDate || carrier.reviewDate;
  if (mcs150Source) {
    mcs150Date = new Date(mcs150Source).toISOString();
    mcs150LastUpdateDate = mcs150Date;
  }
  const nextDue = computeMcs150NextDueDate(dotNumber, mcs150LastUpdateDate);
  mcs150NextDueDate = nextDue ? nextDue.toISOString() : undefined;

  let outOfServiceDate;
  const oosSource = carrier.oosDate || carrier.outOfServiceDate;
  if (oosSource) {
    outOfServiceDate = new Date(oosSource).toISOString();
  }

  const bondRequiredSrc =
    carrier.bondSuretyRequired || carrier.bondInsuranceRequired;
  const bondOnFileSrc =
    carrier.bondSuretyOnFile || carrier.bondInsuranceOnFile;

  const insuranceRequired = !!(
    carrier.bipdInsuranceRequired ||
    carrier.cargoInsuranceRequired ||
    bondRequiredSrc
  );
  const insuranceOnFile = !!(
    carrier.bipdInsuranceOnFile ||
    carrier.cargoInsuranceOnFile ||
    bondOnFileSrc
  );

  return {
    dotNumber,
    mcNumber: carrier.mcNumber ? `MC-${carrier.mcNumber}` : undefined,
    legalName: carrier.legalName || '',
    dbaName: carrier.dbaName || undefined,
    physicalAddress: carrier.phyStreet || '',
    physicalCity: carrier.phyCity || '',
    physicalState: carrier.phyState || '',
    physicalZip: carrier.phyZipcode || '',
    phone: carrier.telephone || undefined,
    operatingStatus,
    authorityType,
    insuranceRequired,
    insuranceOnFile,
    bipdInsuranceRequired: carrier.bipdInsuranceRequired
      ? parseFloat(carrier.bipdInsuranceRequired)
      : undefined,
    bipdInsuranceOnFile: carrier.bipdInsuranceOnFile
      ? parseFloat(carrier.bipdInsuranceOnFile)
      : undefined,
    cargoInsuranceRequired: carrier.cargoInsuranceRequired
      ? parseFloat(carrier.cargoInsuranceRequired)
      : undefined,
    cargoInsuranceOnFile: carrier.cargoInsuranceOnFile
      ? parseFloat(carrier.cargoInsuranceOnFile)
      : undefined,
    bondSuretyRequired: bondRequiredSrc ? parseFloat(bondRequiredSrc) : undefined,
    bondSuretyOnFile: bondOnFileSrc ? parseFloat(bondOnFileSrc) : undefined,
    safetyRating: carrier.safetyRating || undefined,
    totalDrivers: carrier.totalDrivers ? parseInt(carrier.totalDrivers, 10) : undefined,
    totalPowerUnits: carrier.totalPowerUnits
      ? parseInt(carrier.totalPowerUnits, 10)
      : undefined,
    mcs150Date,
    mcs150LastUpdateDate,
    mcs150NextDueDate,
    outOfServiceDate,
  };
}

export function calculateAuthorityHealthScore(authority) {
  let score = 0;

  if (authority.operatingStatus === OperatingStatus.ACTIVE) {
    score += 30;
  }

  if (authority.insuranceRequired) {
    if (authority.insuranceOnFile) {
      let insuranceMeets = true;

      if (authority.bipdInsuranceRequired && authority.bipdInsuranceOnFile) {
        if (authority.bipdInsuranceOnFile < authority.bipdInsuranceRequired) {
          insuranceMeets = false;
        }
      }
      if (authority.cargoInsuranceRequired && authority.cargoInsuranceOnFile) {
        if (authority.cargoInsuranceOnFile < authority.cargoInsuranceRequired) {
          insuranceMeets = false;
        }
      }
      if (authority.bondSuretyRequired && authority.bondSuretyOnFile) {
        if (authority.bondSuretyOnFile < authority.bondSuretyRequired) {
          insuranceMeets = false;
        }
      }

      if (insuranceMeets) {
        score += 25;
      }
    }
  } else {
    score += 25;
  }

  if (authority.safetyRating === 'Satisfactory') {
    score += 20;
  } else if (authority.safetyRating === 'Conditional') {
    score += 10;
  }

  if (authority.mcs150Date) {
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    if (new Date(authority.mcs150Date) >= twoYearsAgo) {
      score += 15;
    }
  }

  if (!authority.outOfServiceDate) {
    score += 10;
  }

  return Math.min(score, 100);
}

/** @returns {Promise<object>} lookupResult with healthScore, or { error: string } */
export async function lookupFmcsaByDotNumber(dotNumber) {
  const apiKey = process.env.REACT_APP_FMCSA_API_KEY;
  if (!apiKey) {
    throw new Error(FMCSA_UNAVAILABLE);
  }

  const trimmed = String(dotNumber).trim();
  const url = `${FMCSA_CARRIER_URL}/${encodeURIComponent(trimmed)}?webKey=${encodeURIComponent(apiKey)}`;

  let response;
  try {
    response = await fetch(url);
  } catch {
    throw new Error(FMCSA_UNAVAILABLE);
  }

  if (response.status === 404) {
    return { error: 'DOT number not found' };
  }
  if (!response.ok) {
    throw new Error(FMCSA_UNAVAILABLE);
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error(FMCSA_UNAVAILABLE);
  }

  if (!data?.content?.carrier) {
    return { error: 'DOT number not found' };
  }

  const mapped = mapCarrierToResult(data.content.carrier);
  return {
    ...mapped,
    healthScore: calculateAuthorityHealthScore(mapped),
  };
}

/** Fields accepted by POST/PATCH /api/authority */
export function toAuthorityPayload(lookupResult) {
  return {
    dotNumber: lookupResult.dotNumber,
    legalName: lookupResult.legalName,
    dbaName: lookupResult.dbaName,
    physicalAddress: lookupResult.physicalAddress,
    physicalCity: lookupResult.physicalCity,
    physicalState: lookupResult.physicalState,
    physicalZip: lookupResult.physicalZip,
    phone: lookupResult.phone,
    mcNumber: lookupResult.mcNumber,
    operatingStatus: lookupResult.operatingStatus,
    statusCode: lookupResult.statusCode,
    authorityType: lookupResult.authorityType,
    mcs150Date: lookupResult.mcs150Date,
    mcs150Mileage: lookupResult.mcs150Mileage,
    mcs150LastUpdateDate: lookupResult.mcs150LastUpdateDate,
    insuranceRequired: lookupResult.insuranceRequired,
    insuranceOnFile: lookupResult.insuranceOnFile,
    bipdInsuranceRequired: lookupResult.bipdInsuranceRequired,
    bipdInsuranceOnFile: lookupResult.bipdInsuranceOnFile,
    cargoInsuranceRequired: lookupResult.cargoInsuranceRequired,
    cargoInsuranceOnFile: lookupResult.cargoInsuranceOnFile,
    bondSuretyRequired: lookupResult.bondSuretyRequired,
    bondSuretyOnFile: lookupResult.bondSuretyOnFile,
    safetyRating: lookupResult.safetyRating,
    totalDrivers: lookupResult.totalDrivers,
    totalPowerUnits: lookupResult.totalPowerUnits,
    outOfServiceDate: lookupResult.outOfServiceDate,
  };
}
